/**
 * Placement maths for SearchableSelect's portal-rendered listbox.
 *
 * Split out of the component so the flip decision is a pure function the tests can
 * exercise directly — jsdom has no layout engine, so nothing about real geometry
 * can be measured in a unit test.
 */

const LISTBOX_GAP = 4;
const LISTBOX_MAX_HEIGHT = 320;
/**
 * Below this the downward listbox is a sliver showing barely one row, which is
 * unusable to scroll (ADO 535) — flip it above the trigger instead of shrinking.
 */
const LISTBOX_MIN_USABLE_HEIGHT = 180;

/**
 * Bottom edge (viewport px) the portal-rendered listbox must not cross when it
 * opens downward. Walks up from the trigger to the nearest scrollable ancestor
 * (e.g. Modal's `Body`, which has `overflow-y: auto` and sits above the Footer as
 * a sibling) and uses its bottom instead of the viewport's — otherwise a tall
 * listbox in a short dialog can render over the Footer's Cancel/Continue buttons,
 * since the portal's z-index sits above the whole modal.
 * Falls back to the viewport bottom when the trigger isn't inside such a container.
 */
export const getClippingBoundaryBottom = (from: HTMLElement): number => {
  let node = from.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      return node.getBoundingClientRect().bottom;
    }
    node = node.parentElement;
  }
  return window.innerHeight;
};

export interface ListboxPlacement {
  /** Exactly one of these is set — see `computeListboxPlacement`. */
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: 'below' | 'above';
}

/**
 * Where to put the fixed-position listbox, given the trigger's viewport rect.
 *
 * Kept pure (no DOM reads) so the flip decision is testable — jsdom has no layout
 * engine, so measuring the real thing in a test proves nothing.
 *
 * Opening downward is clamped to `boundaryBottom` so the listbox can't cover a
 * dialog Footer or a panel's action bar. In a short dialog that space is only a
 * few px (the trigger sits just above the Footer), which is what made the picker
 * render as an unscrollable sliver — so when there isn't a usable amount of room
 * below, flip above the trigger. Upward the bound is the viewport rather than the
 * scroll container: chrome above the trigger (a dialog Header, a panel Header)
 * holds nothing the user needs while the listbox is open, and it closes on
 * outside click or Escape. Flipped, the box is anchored by its `bottom` edge, so
 * a short list still sits against the trigger instead of floating above it.
 */
export const computeListboxPlacement = (
  trigger: { top: number; bottom: number; left: number; width: number },
  boundaryBottom: number,
  viewportHeight: number,
): ListboxPlacement => {
  const spaceBelow = boundaryBottom - trigger.bottom - LISTBOX_GAP;
  const spaceAbove = trigger.top - LISTBOX_GAP;
  const flip = spaceBelow < LISTBOX_MIN_USABLE_HEIGHT && spaceAbove > spaceBelow;

  // Never round the height up to some minimum: on a short viewport the space on
  // the chosen side can be under any floor we'd pick, and a floored flipped box
  // runs off the top of the screen — taking the search input, which renders at the
  // top of the listbox, with it. When neither side has real room we take the
  // larger one and accept a small box: cramped is recoverable (the user can scroll
  // the page or resize), a clipped search input is not.
  const maxHeight = Math.min(LISTBOX_MAX_HEIGHT, Math.max(0, flip ? spaceAbove : spaceBelow));

  return flip
    ? {
        bottom: viewportHeight - trigger.top + LISTBOX_GAP,
        left: trigger.left,
        width: trigger.width,
        maxHeight,
        placement: 'above',
      }
    : {
        top: trigger.bottom + LISTBOX_GAP,
        left: trigger.left,
        width: trigger.width,
        maxHeight,
        placement: 'below',
      };
};
